import TagIds from '~/constants/common/tagIds';
import { useGetVehicleDetails } from '~/context/vdp/VehicleContext';
import { isDefined } from '~/utilities/array';
import { FeatureTourHighlightCards } from './constants';
import AutoCheckLink from '../AutoCheckLink';
import CarfaxLink from '../CarfaxLink';

type CallToActionProps =
  | React.HTMLProps<HTMLAnchorElement>
  | React.HTMLProps<HTMLButtonElement>;

export type FeatureTourHighlightInfo = {
  cardType: FeatureTourHighlightCards;
  title: string;
  subtitle?: string;
  imgProps?: React.ImgHTMLAttributes<HTMLImageElement>;
  listItems?: string[];
  callToAction?: (props: CallToActionProps) => React.JSX.Element;
  condition: boolean;
};

const cdnUrl = process.env.CVNA_APP_CDN_ASSETS_PATH;

export default function useGetFeatureTourHighlights(): FeatureTourHighlightInfo[] {
  const {
    highlights = [],
    remainingWarrantyMonths = 0,
    remainingWarrantyMiles = 0,
    remainingDriveTrainWarrantyMiles = 0,
    remainingDriveTrainWarrantyMonths = 0,
    remainingBatteryWarrantyMiles = 0,
    remainingBatteryWarrantyMonths = 0
  } = useGetVehicleDetails();
  const showNoAccidents = highlights?.some(
    highlight => highlight.tagId === TagIds.NoReportedAccidents
  );
  const hasOriginalWarranty =
    remainingWarrantyMonths > 1 && remainingWarrantyMiles > 0;
  const hasPowerTrainWarranty =
    remainingDriveTrainWarrantyMonths > 0 &&
    remainingDriveTrainWarrantyMiles > 1;
  const hasBatteryWarranty =
    remainingBatteryWarrantyMiles > 0 && remainingBatteryWarrantyMonths > 0;
  const showSingleOwner = highlights?.some(
    highlight => highlight.tagId === TagIds.SingleOwner
  );
  const hasLowMileageTag = highlights?.some(
    highlight => highlight.tagId === TagIds.LowMileage
  );
  const hasLowMilesPerYearTag = highlights?.some(
    highlight => highlight.tagId === TagIds.LowMilesPerYear
  );

  return [
    {
      cardType: FeatureTourHighlightCards.NoAccident,
      title: 'No Reported Accidents',
      imgProps: {
        src: `${cdnUrl}/svgs/carfax.svg`,
        alt: 'Carfax Logo'
      },
      callToAction: props => {
        return (
          <CarfaxLink
            {...props}
            data-analytics-id="Highlights Card"
            data-analytics-attributes={JSON.stringify({
              card: FeatureTourHighlightCards.NoAccident,
              cta: 'View Report'
            })}
            className="tl-label-m"
          >
            View Report
          </CarfaxLink>
        );
      },
      condition: showNoAccidents
    },
    {
      cardType: FeatureTourHighlightCards.Warranty,
      title: 'Original Warranty Still Active',
      listItems: [
        hasOriginalWarranty ? 'Basic warranty' : null,
        hasPowerTrainWarranty ? 'Powertrain warranty' : null,
        hasBatteryWarranty ? 'Battery warranty' : null
      ].filter(isDefined),
      condition: hasOriginalWarranty || hasPowerTrainWarranty
    },
    {
      cardType: FeatureTourHighlightCards.SingleOwner,
      title: 'Single Owner',
      subtitle: 'History reports show only one prior owner',
      callToAction: () => (
        <AutoCheckLink
          className="mt-auto w-fit tl-label-m"
          data-analytics-id="Highlights Card"
          data-analytics-attributes={JSON.stringify({
            card: FeatureTourHighlightCards.SingleOwner,
            cta: 'Details'
          })}
        >
          Details
        </AutoCheckLink>
      ),
      condition: showSingleOwner
    },
    {
      cardType: FeatureTourHighlightCards.LowMileage,
      title: hasLowMileageTag ? 'Low Mileage' : 'Low Annual Mileage',
      listItems: [
        hasLowMilesPerYearTag
          ? 'Less than 9,000 miles per year on average'
          : null,
        hasLowMileageTag ? 'Less than 20,000 miles' : null
      ].filter(isDefined),
      condition: hasLowMileageTag || hasLowMilesPerYearTag
    }
  ].filter(item => item.condition);
}
